import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type {
  ITextMediaBlockAction,
  ITextMediaBlockImage,
  ITextMediaBlockLink,
  ITextMediaBlockProps,
  ITextMediaBlockVideo,
} from "./TextMediaBlock.props";
import { RichTextHtml } from "../../RichTextHtml";
import { TextMediaVideo } from "./TextMediaVideo.client";

const getSafeRel = (link: Pick<ITextMediaBlockLink, "target">) =>
  link.target === "_blank" ? "noopener noreferrer" : undefined;

const getObjectPosition = (image: ITextMediaBlockImage) =>
  image.focalPoint ? `${image.focalPoint.x}% ${image.focalPoint.y}%` : "center";

const lightTheme = {
  body: "text-[#334155] [&_a]:text-[#2144FF] [&_a]:decoration-[#2144FF]/25 [&_a:hover]:decoration-[#2144FF] [&_strong]:text-[#151927]",
  caption: "text-[#667085]",
  copy: "text-[#4B5568]",
  eyebrow: "border-[#D9E1FF] bg-[#F6F1FF] text-[#2144FF]",
  heading: "text-[#151927]",
  section: "text-[#151927]",
};

const ActionLink = ({ action }: { action: ITextMediaBlockAction }) => {
  const Link = resolveKaajPrimitive("Link");
  const style = action.style;
  const isTextLink = style === "link";

  return (
    <Link
      aria-label={action.ariaLabel}
      className={cn(
        "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition-[background-color,border-color,box-shadow,color,transform] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none max-[420px]:w-full",
        "focus-visible:outline-[#2144FF]",
        style === "primary" &&
          "bg-[#2144FF] text-white shadow-[0_14px_34px_rgba(33,68,255,0.25)] hover:bg-[#132EDB] motion-safe:hover:-translate-y-0.5",
        style === "secondary" &&
          "border border-[#D9E1FF] bg-white text-[#2144FF] shadow-[0_12px_30px_rgba(33,68,255,0.1)] hover:border-[#2144FF] hover:bg-[#F6F1FF]",
        style === "outline" &&
          "border border-[#C9D6FF] bg-white/80 text-[#2144FF] hover:border-[#2144FF] hover:bg-white",
        style === "ghost" && "bg-[#F6F1FF] text-[#2144FF] hover:bg-[#EEE6FF]",
        isTextLink &&
          "min-h-0 px-0 py-0 text-[#2144FF] underline decoration-[#2144FF]/25 underline-offset-4 hover:text-[#132EDB] hover:decoration-[#132EDB] max-[420px]:w-auto",
      )}
      href={action.href}
      rel={getSafeRel(action)}
      target={action.target}
    >
      {action.text}
      {style === "primary" || isTextLink ? (
        <span aria-hidden="true" className="ml-2">
          →
        </span>
      ) : null}
    </Link>
  );
};

const ImagePanel = ({ image }: { image: ITextMediaBlockImage }) => {
  const Image = resolveKaajPrimitive("Image");
  const altText = image.alternateText;

  return (
    <div className="relative overflow-hidden rounded-[1.75rem] border border-[#D9E1FF] bg-white p-2 shadow-[0_22px_64px_rgba(33,68,255,0.11)]">
      <div className="relative overflow-hidden rounded-[1.35rem] bg-[#FAFBFF]">
        <Image
          alt={altText}
          className="aspect-[4/3] h-full w-full object-cover"
          decoding="async"
          height={image.height ?? undefined}
          loading="lazy"
          sizes={image.sizes ?? "(min-width: 1280px) 540px, (min-width: 1024px) 48vw, 92vw"}
          src={image.defaultSrc}
          srcSet={image.srcSet ?? undefined}
          style={{ objectPosition: getObjectPosition(image) }}
          width={image.width ?? undefined}
        />
      </div>
    </div>
  );
};

const VideoPanel = ({ video }: { video: ITextMediaBlockVideo }) => {
  const poster = video.poster;

  if (!video.embedUrl && !poster?.defaultSrc) {
    return null;
  }

  return (
    <div className="relative overflow-hidden rounded-[1.75rem] border border-[#253158] bg-[#101322] p-2 shadow-[0_24px_70px_rgba(21,25,39,0.2)]">
      <div
        className="relative overflow-hidden rounded-[1.35rem] bg-[#101322]"
        style={video.aspectRatio ? { aspectRatio: video.aspectRatio } : undefined}
      >
        <TextMediaVideo poster={poster ?? undefined} video={video} />
      </div>
    </div>
  );
};

const MediaColumn = ({
  caption,
  captionClassName,
  image,
  video,
}: {
  caption?: string | null;
  captionClassName: string;
  image?: ITextMediaBlockImage | null;
  video?: ITextMediaBlockVideo | null;
}) => {
  if (!image && !video) {
    return null;
  }

  return (
    <figure className="relative min-w-0">
      <div className="grid gap-4 lg:gap-5">
        {image ? <ImagePanel image={image} /> : null}
        {video ? <VideoPanel video={video} /> : null}
      </div>
      {caption ? (
        <figcaption
          className={cn("mt-4 max-w-[58ch] space-y-1.5 text-sm leading-6", captionClassName)}
        >
          <p>{caption}</p>
        </figcaption>
      ) : null}
    </figure>
  );
};

export const TextMediaBlock = ({
  anchorId,
  buttons,
  direction,
  eyebrow,
  heading,
  media,
  parentClass,
  spacing,
  subheading,
}: ITextMediaBlockProps) => {
  const mediaFirstOnDesktop = direction === "ltr";
  const blockId = anchorId ?? undefined;
  const headingId = blockId && heading ? `${blockId}-heading` : undefined;
  const styles = lightTheme;
  const image = media?.type === "image" ? media.image?.image : undefined;
  const imageCaption = media?.type === "image" ? media.image?.caption : undefined;
  const video = media?.type === "video" ? media.video : undefined;
  const visibleActions = buttons ?? [];
  const hasContent = Boolean(eyebrow || heading || subheading || visibleActions.length);
  const hasMedia = Boolean(image || video);

  if (!hasContent && !hasMedia) {
    return null;
  }

  const mediaColumn = hasMedia ? (
    <MediaColumn
      caption={imageCaption ?? video?.caption}
      captionClassName={styles.caption}
      image={image}
      video={video}
    />
  ) : null;

  const contentColumn = hasContent ? (
    <div className={cn("max-w-[36rem]", mediaFirstOnDesktop && "lg:ml-auto")}>
      {eyebrow ? (
        <p
          className={cn(
            "mb-5 inline-flex max-w-full rounded-full border px-3 py-1.5 text-xs font-bold leading-5 [overflow-wrap:anywhere]",
            styles.eyebrow,
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      {heading ? (
        <h2 className={cn("kaaj-section-heading", styles.heading)} id={headingId}>
          {heading}
        </h2>
      ) : null}
      {subheading ? (
        <RichTextHtml
          className={cn(
            "mt-5 max-w-[58ch] text-pretty text-lg leading-8 [overflow-wrap:anywhere]",
            styles.copy,
          )}
          html={subheading}
        />
      ) : null}
      {visibleActions.length ? (
        <div className="mt-8 flex flex-wrap items-center gap-3 max-[420px]:w-full max-[420px]:justify-center">
          {visibleActions.map((action, index) => (
            <ActionLink action={action} key={`${action.href}-${action.text}-${index}`} />
          ))}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden",
        styles.section,
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      id={blockId}
    >
      <div className="kaaj-container relative">
        <div
          className={cn(
            "grid gap-10 lg:items-center",
            hasContent && hasMedia
              ? "lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-14 xl:gap-16"
              : "lg:grid-cols-1",
          )}
        >
          {contentColumn ? (
            <div className={cn(mediaFirstOnDesktop && "lg:order-2")}>{contentColumn}</div>
          ) : null}
          {mediaColumn ? (
            <div className={cn(mediaFirstOnDesktop && "lg:order-1")}>{mediaColumn}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

TextMediaBlock.displayName = "Block::TextMediaBlock";
